import { useEffect, useState, useCallback } from 'react';
import { adminApi } from '../../lib/api';
import type { AuditEntry } from '../../lib/api';
import { useMobile } from '../../hooks/useMobile';
import { formatDistanceToNowStrict, format } from 'date-fns';
import { Search, Download, ChevronUp, ChevronDown } from '../../lib/icons';

const PAGE_SIZE = 25;

// Action category prefixes for the filter dropdown
const ACTION_CATEGORIES = [
  { value: 'all',      label: 'All actions' },
  { value: 'message',  label: 'Messages' },
  { value: 'thread',   label: 'Threads' },
  { value: 'user',     label: 'Users' },
  { value: 'task',     label: 'Tasks' },
  { value: 'settings', label: 'Settings' },
];

function actionLabel(action: string): string {
  return action.replace(/\./g, ' › ').replace(/_/g, ' ');
}

function actionColor(action: string): string {
  if (action.startsWith('user'))     return 'var(--blue)';
  if (action.startsWith('message'))  return 'var(--v2)';
  if (action.startsWith('thread'))   return 'var(--amber)';
  if (action.startsWith('task'))     return 'var(--purple)';
  if (action.startsWith('settings')) return 'var(--teal-300)';
  return 'var(--shell-xs)';
}

function actionBg(action: string): string {
  if (action.startsWith('user'))     return 'rgba(55,138,221,.15)';
  if (action.startsWith('message'))  return 'rgba(29,158,117,.15)';
  if (action.startsWith('thread'))   return 'rgba(239,159,39,.15)';
  if (action.startsWith('task'))     return 'rgba(159,122,234,.15)';
  if (action.startsWith('settings')) return 'rgba(159,225,203,.15)';
  return 'var(--hover-strong)';
}

export default function AuditLogTab() {
  const isMobile = useMobile();
  const [entries, setEntries]       = useState<AuditEntry[]>([]);
  const [total, setTotal]           = useState(0);
  const [page, setPage]             = useState(1);
  const [loading, setLoading]       = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [search, setSearch]         = useState('');
  const [category, setCategory]     = useState('all');
  const [from, setFrom]             = useState('');
  const [to, setTo]                 = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Filters that hit the server. Search is debounced separately below.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(id);
  }, [search]);

  // Reset to page 1 whenever a filter changes
  useEffect(() => { setPage(1); }, [category, debouncedSearch, from, to]);

  const filterParams = useCallback(() => ({
    category,
    search: debouncedSearch,
    from: from || undefined,
    to: to ? `${to}T23:59:59` : undefined,
  }), [category, debouncedSearch, from, to]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.auditLog({ page, pageSize: PAGE_SIZE, ...filterParams() });
      setEntries(data.entries);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  }, [page, filterParams]);

  useEffect(() => { load(); }, [load]);

  async function download() {
    setDownloading(true);
    try {
      const { data } = await adminApi.auditExport(filterParams());
      const url = URL.createObjectURL(data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-log-${format(new Date(), 'yyyy-MM-dd-HHmm')}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  }

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const isFiltered = category !== 'all' || !!debouncedSearch || !!from || !!to;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* Toolbar */}
      <div style={{
        padding: isMobile ? '10px 12px' : '12px 20px',
        borderBottom: '1px solid var(--shell-border)',
        display: 'flex', gap: 8, flexWrap: 'wrap', flexShrink: 0, alignItems: 'center',
      }}>
        {/* Search */}
        <div style={{ flex: 1, minWidth: 150, position: 'relative' }}>
          <Search size={13} strokeWidth={1.8} style={{
            position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)',
            color: 'var(--shell-xs)', pointerEvents: 'none',
          }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search action, actor…"
            style={{
              width: '100%', background: 'var(--vs2)',
              border: '1px solid var(--shell-border)', borderRadius: 4,
              padding: '7px 10px 7px 28px', color: 'var(--shell-text)',
              fontSize: '0.8rem', outline: 'none',
            }}
          />
        </div>

        {/* Category filter */}
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          style={{
            background: 'rgba(29,158,117,.2)', border: 'none',
            borderRadius: 4, padding: '7px 10px', color: 'var(--v2)',
            fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', outline: 'none',
          }}
        >
          {ACTION_CATEGORIES.map((c) => (
            <option key={c.value} value={c.value} style={{ background: 'var(--vs1)', color: 'var(--v2)' }}>{c.label}</option>
          ))}
        </select>

        {/* Date range */}
        <input
          type="date" value={from} onChange={(e) => setFrom(e.target.value)}
          title="From date"
          style={dateInputStyle}
        />
        <input
          type="date" value={to} onChange={(e) => setTo(e.target.value)}
          title="To date"
          style={dateInputStyle}
        />

        {/* Download */}
        <button
          onClick={download}
          disabled={downloading}
          title={isFiltered ? 'Download the filtered set as CSV' : 'Download the entire log as CSV'}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'rgba(55,138,221,.18)', border: '1px solid var(--shell-border)',
            borderRadius: 4, padding: '7px 12px', color: 'var(--blue)',
            fontSize: '0.78rem', fontWeight: 600, cursor: downloading ? 'wait' : 'pointer',
            whiteSpace: 'nowrap', opacity: downloading ? 0.6 : 1,
          }}
        >
          <Download size={13} strokeWidth={1.8} />
          {downloading ? 'Preparing…' : `Download ${isFiltered ? 'filtered' : 'all'}`}
        </button>
      </div>

      {/* Count */}
      <div style={{
        padding: '6px 20px', fontSize: '0.72rem', color: 'var(--shell-xs)',
        borderBottom: '1px solid var(--row-divider)', flexShrink: 0,
      }}>
        {total} {total === 1 ? 'entry' : 'entries'}{isFiltered ? ' (filtered)' : ''} · every note, participant change and delivery is recorded
      </div>

      {/* Log entries */}
      <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '8px 8px' : '8px 12px' }}>
        {loading && (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--shell-xs)', fontSize: '0.82rem' }}>
            Loading…
          </div>
        )}

        {!loading && entries.length === 0 && (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--shell-xs)', fontSize: '0.82rem' }}>
            No entries match your filter.
          </div>
        )}

        {!loading && entries.map((entry) => (
          <AuditRow
            key={entry.id}
            entry={entry}
            isMobile={isMobile}
            expanded={expandedId === entry.id}
            onToggle={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
          />
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{
          borderTop: '1px solid var(--shell-border)', padding: '10px 16px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
        }}>
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1 || loading}
            style={paginationBtn(page === 1 || loading)}
          >
            ← Prev
          </button>
          <span style={{ fontSize: '0.78rem', color: 'var(--shell-xs)' }}>
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages || loading}
            style={paginationBtn(page === totalPages || loading)}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

function AuditRow({ entry, isMobile, expanded, onToggle }: {
  entry: AuditEntry;
  isMobile: boolean;
  expanded: boolean;
  onToggle: () => void;
}) {
  const actor = entry.actor_name ?? entry.actor_system ?? 'System';
  const hasMetadata = entry.metadata && Object.keys(entry.metadata).length > 0;

  return (
    <div
      style={{
        borderBottom: '1px solid var(--row-divider)',
        padding: isMobile ? '9px 6px' : '8px 8px',
        cursor: hasMetadata ? 'pointer' : 'default',
        transition: 'background .1s',
        borderRadius: 3,
      }}
      onClick={hasMetadata ? onToggle : undefined}
      onMouseEnter={(e) => { if (hasMetadata) (e.currentTarget as HTMLDivElement).style.background = 'var(--hover)'; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
    >
      {/* Main row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>

        {/* Action badge */}
        <span style={{
          fontSize: '0.72rem', fontWeight: 700, padding: '3px 7px', borderRadius: 3,
          background: actionBg(entry.action), color: actionColor(entry.action),
          whiteSpace: 'nowrap', flexShrink: 0,
        }}>
          {actionLabel(entry.action)}
        </span>

        {/* Actor */}
        <span style={{ fontSize: '0.78rem', color: 'var(--shell-muted)', whiteSpace: 'nowrap' }}>
          {actor}
        </span>

        {/* Entity */}
        {entry.entity_type && (
          <span style={{ fontSize: '0.73rem', color: 'var(--shell-xs)', whiteSpace: 'nowrap' }}>
            {entry.entity_type}
          </span>
        )}

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Timestamp */}
        <span
          title={format(new Date(entry.created_at), 'dd MMM yyyy HH:mm:ss')}
          style={{ fontSize: '0.7rem', color: 'var(--shell-xs)', whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          {formatDistanceToNowStrict(new Date(entry.created_at), { addSuffix: true })}
        </span>

        {/* Expand indicator */}
        {hasMetadata && (
          <span style={{ color: 'var(--shell-xs)', flexShrink: 0, display: 'flex' }}>
            {expanded ? <ChevronUp size={13} strokeWidth={1.8} /> : <ChevronDown size={13} strokeWidth={1.8} />}
          </span>
        )}
      </div>

      {/* Expanded metadata */}
      {expanded && hasMetadata && (
        <pre style={{
          marginTop: 8, padding: '8px 10px',
          background: 'rgba(0,0,0,.25)', borderRadius: 4,
          fontSize: '0.72rem', color: 'var(--teal-300)',
          overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
          fontFamily: 'var(--font-mono)',
          maxHeight: 180, overflowY: 'auto',
        }}>
          {JSON.stringify(entry.metadata, null, 2)}
        </pre>
      )}
    </div>
  );
}

const dateInputStyle: React.CSSProperties = {
  background: 'var(--vs2)', border: '1px solid var(--shell-border)',
  borderRadius: 4, padding: '6px 8px', color: 'var(--shell-text)',
  fontSize: '0.76rem', outline: 'none', colorScheme: 'light dark',
};

function paginationBtn(disabled: boolean): React.CSSProperties {
  return {
    background: 'var(--vs2)', border: '1px solid var(--shell-border)',
    borderRadius: 4, padding: '6px 14px', color: disabled ? 'var(--shell-xs)' : 'var(--shell-text)',
    fontSize: '0.78rem', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.4 : 1,
  };
}
